import { Alert, Anchor, Badge, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { brand } from '../theme/brand';

export interface RecordFact {
  label: string;
  value: ReactNode;
  /** Renders in muted type when there is genuinely nothing recorded, rather than an empty gap. */
  empty?: string;
}

export interface RecordSection {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export interface RecordScreenProps {
  backTo: string;
  backLabel: string;
  title: string;
  subtitle?: string;
  status?: { label: string; color?: string };
  facts?: RecordFact[];
  /**
   * One line under the facts, for what a figure beside it MEANS.
   *
   * A caveat that belongs to a number belongs next to the number. The first of these is a partner's
   * open balance, which is worked out again every night while it is unpaid — a fact the owner has
   * to know before he reads the figure out to somebody, and one that in a dialog or a tooltip is a
   * fact nobody reads at all.
   */
  factsNote?: ReactNode;
  sections: RecordSection[];
  actions?: ReactNode;
  isLoading?: boolean;
  notFound?: boolean;
  notFoundMessage?: string;
  /**
   * The record could not be READ — which is a third thing, and must never be shown as either of
   * the other two. A failed request leaves every query holding no data, so a screen that only asks
   * "is it loading, and did I get a row?" answers a five-second outage by telling the owner his
   * record has been deleted, or by drawing the record with every field it could not fetch left
   * blank. Both are read as facts about the business rather than facts about the network.
   */
  loadFailed?: boolean;
  loadFailedMessage?: string;
  /** Rendered after the sections, for blocks that bring their own surface (a CrudSection). */
  children?: ReactNode;
}

/**
 * The shell for looking at one thing.
 *
 * Until this existed the portal could only express "a list of a collection", so answering
 * "what do we pay McKenzie, and since when?" meant reading a name off one table and carrying an
 * identifier to another. A record screen is the missing half: a subject, the facts about it, and
 * the related history gathered underneath — the shape people actually think in.
 *
 * Deliberately presentational. It fetches nothing, so each screen stays in charge of its own
 * queries and this stays reusable for suppliers, referral partners and stock items as those are
 * needed, without becoming a framework.
 */
export function RecordScreen(props: RecordScreenProps) {
  const facts = (props.facts ?? []).filter(Boolean);

  return (
    <Stack gap="md">
      <Anchor component={Link} to={props.backTo} size="sm" c={brand.sea}>
        ← {props.backLabel}
      </Anchor>

      {props.isLoading ? (
        <Group gap="xs" py="xl" justify="center">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">
            Loading…
          </Text>
        </Group>
      ) : props.loadFailed ? (
        <Alert color="red" title="This could not be loaded">
          {props.loadFailedMessage ??
            'Nothing has been lost and nothing has changed — it could not be read just now. Refresh the page to try again.'}
        </Alert>
      ) : props.notFound ? (
        <Alert color="yellow" title="Not found">
          {props.notFoundMessage ?? 'This record no longer exists. It may have been removed.'}
        </Alert>
      ) : (
        <>
          <Paper withBorder radius="md" p="lg">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <div style={{ minWidth: 0 }}>
                <Group gap="sm" align="center">
                  <Title order={3} style={{ fontFamily: brand.font, letterSpacing: '-0.01em' }}>
                    {props.title}
                  </Title>
                  {props.status && (
                    <Badge color={props.status.color ?? 'gray'} variant="light">
                      {props.status.label}
                    </Badge>
                  )}
                </Group>
                {props.subtitle && (
                  <Text c="dimmed" size="sm" mt={2}>
                    {props.subtitle}
                  </Text>
                )}
              </div>
              {props.actions && <Group gap="xs">{props.actions}</Group>}
            </Group>

            {facts.length > 0 && (
              <Group
                mt="lg"
                gap="xl"
                wrap="wrap"
                style={{ borderTop: `1px solid ${brand.haze}`, paddingTop: 'var(--mantine-spacing-md)' }}
              >
                {facts.map((fact) => (
                  <div key={fact.label} style={{ minWidth: 120 }}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
                      {fact.label}
                    </Text>
                    <Text size="sm" fw={500} mt={2}>
                      {fact.value ?? (
                        <Text span size="sm" c="dimmed" fs="italic">
                          {fact.empty ?? 'Not recorded'}
                        </Text>
                      )}
                    </Text>
                  </div>
                ))}
              </Group>
            )}
            {props.factsNote && (
              <Text size="xs" c="dimmed" mt="sm">
                {props.factsNote}
              </Text>
            )}
          </Paper>

          {props.sections.map((section) => (
            <Paper key={section.title} withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
                <div>
                  <Title order={5}>{section.title}</Title>
                  {section.description && (
                    <Text size="xs" c="dimmed" mt={2}>
                      {section.description}
                    </Text>
                  )}
                </div>
                {section.actions && <Group gap="xs">{section.actions}</Group>}
              </Group>
              {section.children}
            </Paper>
          ))}
          {props.children}
        </>
      )}
    </Stack>
  );
}

/** Consistent "there is nothing here yet, and here is what to do" copy inside a section. */
export function EmptyNote({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" c="dimmed" fs="italic">
      {children}
    </Text>
  );
}
